import React, { Component } from "react";
import StoreBoardService from "../../services/storeboardservice";

// Store Board (Audit -> Store Board) — one live view of every billed order and where the
// money is. Reads Admin_GetStoreBoard on db37 (gateway-accurate tender split: mis-booked
// "cash" UPI is reclassified by remark so the split ties to collected). Styles scoped under
// .sb-root; brand pink #fe1d7f (backend palette). Aggregates are real; order rows are live.
const INR = (n) => "₹" + Math.round(n || 0).toLocaleString("en-IN");
const INRk = (n) => {
  n = Math.round(n || 0);
  if (Math.abs(n) >= 100000) return "₹" + (n / 100000).toFixed(2).replace(/\.00$/, "") + "L";
  if (Math.abs(n) >= 1000) return "₹" + Math.round(n / 1000) + "k";
  return "₹" + n;
};
const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
const dshort = (s) => {
  if (!s) return "—";
  const d = new Date(s + "T12:00:00");
  return isNaN(d) ? s : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
// weekday + date, e.g. "Wed, 10 Sep"
const ddow = (s) => {
  if (!s) return "—";
  const d = new Date(s + "T12:00:00");
  return isNaN(d) ? s : WD[d.getDay()] + ", " + d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
};

// tender-mode -> colour (CSS var)
const TENDER_COLOR = {
  "Paytm QR": "var(--accent)",
  PhonePe: "var(--accent2)",
  "UPI / Online": "var(--blue)",
  "LK Wallet": "var(--violet)",
  Cash: "var(--gold)",
  Other: "var(--slate)",
};
// custody stage -> colour (matches Admin_GetStoreBoard SplitOrderStatus mapping)
const STAGE_COLOR = {
  "Picked up": "--slate",
  "At store": "--slate",
  "Sent to factory": "--violet",
  "At factory": "--blue",
  Returning: "--gold",
  "Ready for delivery": "--green",
  "Payment pending": "--amber",
  Delivered: "--muted",
  Cancelled: "--red",
};
const AGE_COLOR = { "0-2 days": "var(--green)", "3-7 days": "var(--amber)", "8-30 days": "var(--red)", "30+ days": "var(--red)" };
// deterministic ordering (MySQL JSON_ARRAYAGG does not preserve subquery order)
const STAGE_ORDER = ["Picked up", "At store", "Sent to factory", "At factory", "Returning", "Ready for delivery", "Payment pending", "Delivered", "Cancelled"];
const BUCKET_ORDER = ["0-2 days", "3-7 days", "8-30 days", "30+ days"];
const byAmountDesc = (k) => (a, b) => (b[k] || 0) - (a[k] || 0);

export default class StoreBoard extends Component {
  constructor(props) {
    super(props);
    const now = new Date();
    this.state = {
      from: ymd(new Date(now.getFullYear(), now.getMonth(), 1)),
      to: ymd(now),
      storeId: 0,
      basis: "billing", // "billing" = by invoice date | "order" = by order-created date
      activeRange: "month",
      board: null,
      loading: true,
      error: "",
      refreshedAt: null,
      // orders-table UI state
      q: "",
      tableStore: "",
      tablePay: "",     // "", "paid", "unpaid"
      tableStatus: "",  // "" or a stage label
      odOnly: false,
      sort: "billed",
      dir: -1,
    };
  }

  componentDidMount() {
    this.load();
  }

  setRange = (key) => {
    const now = new Date();
    let from = new Date(now), to = new Date(now);
    if (key === "today") { /* from=to=today */ }
    else if (key === "yesterday") { from.setDate(now.getDate() - 1); to.setDate(now.getDate() - 1); }
    else if (key === "7d") { from.setDate(now.getDate() - 6); }
    else if (key === "month") { from = new Date(now.getFullYear(), now.getMonth(), 1); }
    else if (key === "lastmonth") { from = new Date(now.getFullYear(), now.getMonth() - 1, 1); to = new Date(now.getFullYear(), now.getMonth(), 0); }
    this.setState({ activeRange: key, from: ymd(from), to: ymd(to) }, this.load);
  };

  load = async () => {
    this.setState({ loading: true, error: "" });
    const b = await this.service_getBoard();
    if (b && b.kpi) {
      this.setState({ board: b, loading: false, refreshedAt: new Date(), tableStore: "" });
    } else {
      this.setState({
        loading: false,
        error: (b && b.message) || "Could not load the Store Board. Please try again.",
      });
    }
  };

  service_getBoard = () => {
    if (!this.service) this.service = new StoreBoardService();
    return this.service.getBoard(this.state.from, this.state.to, this.state.storeId, this.state.basis);
  };

  setBasis = (b) => {
    if (b === this.state.basis) return;
    this.setState({ basis: b }, this.load);
  };

  // ---- derived table rows (client-side filter + sort) ----
  tableRows = () => {
    const b = this.state.board;
    if (!b || !b.orders) return [];
    const { q, tableStore, tablePay, tableStatus, odOnly, sort, dir } = this.state;
    const ql = (q || "").toLowerCase();
    let rows = b.orders.filter(
      (o) =>
        (!tableStore || o.store === tableStore) &&
        (!tablePay || (tablePay === "paid" ? o.paid : !o.paid)) &&
        (!tableStatus || o.stage === tableStatus) &&
        (!odOnly || o.overdue) &&
        (!ql || ("" + o.id + (o.customer || "") + (o.mobile || "") + (o.store || "") + (o.service || "")).toLowerCase().indexOf(ql) >= 0)
    );
    rows.sort((a, b2) => {
      let x = a[sort], y = b2[sort];
      if (typeof x === "string") { x = x.toLowerCase(); y = (y || "").toLowerCase(); }
      return (x < y ? -1 : x > y ? 1 : 0) * dir;
    });
    return rows;
  };

  onSort = (k) => {
    this.setState((s) => ({ sort: k, dir: s.sort === k ? -s.dir : 1 }));
  };

  render() {
    const { board, loading, error, refreshedAt, activeRange } = this.state;
    const kpi = board && board.kpi ? board.kpi : {};
    const outstanding = (kpi.billed || 0) - (kpi.collected || 0);
    const collPct = kpi.billed ? Math.round((kpi.collected / kpi.billed) * 100) : 0;
    const stores = (board && board.stores) || [];
    const chips = [
      ["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"],
      ["month", "This month"], ["lastmonth", "Last month"],
    ];

    return (
      <div className="sb-root">
        <style>{CSS}</style>
        <div className="sb-wrap">
          <div className="sb-eyebrow">Reports</div>
          <div className="sb-h2">
            Store Board{" "}
            <span className="sb-dim">
              · {this.state.storeId === 0 ? "All stores" : (stores.find((s) => s.id === this.state.storeId) || {}).name || "Store"}
            </span>
          </div>
          <p className="sb-lede">
            Revenue, GST, order pipeline, receivables, customers and payments — plus every order and where it sits right now.
            Payments are shown by the tender that actually collected them (online "cash" reclassified from remarks).
          </p>

          {/* basis toggle */}
          <div className="sb-basis">
            <span className="sb-basis-lab">Count orders by</span>
            <div className="sb-seg">
              <button className="sb-segbtn" aria-pressed={this.state.basis === "billing"} onClick={() => this.setBasis("billing")}>Billing date</button>
              <button className="sb-segbtn" aria-pressed={this.state.basis === "order"} onClick={() => this.setBasis("order")}>Order date</button>
            </div>
            <span className="sb-basis-note">
              {this.state.basis === "billing"
                ? "Orders billed (invoiced) in the period — revenue & collections view."
                : "Orders placed (created) in the period — intake view, matches Daily Order Summary."}
            </span>
          </div>

          {/* controls */}
          <div className="sb-controls">
            {chips.map(([k, l]) => (
              <button key={k} className="sb-rchip" aria-pressed={activeRange === k} onClick={() => this.setRange(k)}>
                {l}
              </button>
            ))}
          </div>
          <div className="sb-crow">
            <div className="sb-field">
              <label>From</label>
              <input type="date" value={this.state.from} onChange={(e) => this.setState({ from: e.target.value, activeRange: "" })} />
            </div>
            <div className="sb-dash">–</div>
            <div className="sb-field">
              <label>To</label>
              <input type="date" value={this.state.to} onChange={(e) => this.setState({ to: e.target.value, activeRange: "" })} />
            </div>
            <div className="sb-field">
              <label>Store</label>
              <select value={this.state.storeId} onChange={(e) => this.setState({ storeId: parseInt(e.target.value, 10) })}>
                <option value={0}>★ All stores (combined)</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <button className="sb-apply" onClick={this.load} disabled={loading}>
              {loading ? "Loading…" : "Apply"}
            </button>
          </div>
          <p className="sb-showing">
            Showing <b>{ddow(this.state.from)} – {ddow(this.state.to)}</b>
            <span className="sb-basis-chip">by {this.state.basis === "order" ? "order date" : "billing date"}</span>
            {refreshedAt ? <span> · refreshed {refreshedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span> : null}
          </p>

          {error ? <div className="sb-err">{error}</div> : null}
          {loading && !board ? <div className="sb-loading">Loading Store Board…</div> : null}

          {board && board.kpi ? (
            <React.Fragment>
              {/* KPI row 1 */}
              <div className="sb-grid sb-g3" style={{ marginTop: 14 }}>
                <div className="sb-card sb-kpi"><div className="sb-lab">Billed</div><div className="sb-big">{INR(kpi.billed)}</div><div className="sb-sub">{(kpi.orders || 0).toLocaleString("en-IN")} orders</div></div>
                <div className="sb-card sb-kpi"><div className="sb-lab">Collected</div><div className="sb-big" style={{ color: "var(--green)" }}>{INR(kpi.collected)}</div><div className="sb-sub">{collPct}% of billed · paid so far</div></div>
                <div className="sb-card sb-kpi"><div className="sb-lab">Outstanding</div><div className="sb-big" style={{ color: "var(--amber)" }}>{INR(outstanding)}</div><div className="sb-sub">billed − collected · {100 - collPct}% unpaid</div></div>
              </div>
              <p className="sb-kpi-note">Collected is the paid portion of Billed — <b>Billed = Collected + Outstanding</b> ({INR(kpi.billed)} = {INR(kpi.collected)} + {INR(outstanding)}).</p>
              <div className="sb-grid sb-g3" style={{ marginTop: 14 }}>
                <div className="sb-card sb-kpi"><div className="sb-lab">Orders</div><div className="sb-big">{(kpi.orders || 0).toLocaleString("en-IN")}</div><div className="sb-sub">billed</div></div>
                <div className="sb-card sb-kpi"><div className="sb-lab">Garments</div><div className="sb-big">{(kpi.garments || 0).toLocaleString("en-IN")}</div><div className="sb-sub">{INR((kpi.billed || 0) / (kpi.garments || 1))} / garment</div></div>
                <div className="sb-card sb-kpi"><div className="sb-lab">Avg order</div><div className="sb-big">{INR(kpi.avgOrder)}</div><div className="sb-sub">per order</div></div>
              </div>

              {this.renderStores(stores)}
              {this.renderRevenue(board.revenueByService || [])}
              {this.renderTenders(board.tenders || [], kpi.collected || 0)}
              {this.renderPipeline(board.pipeline || [])}

              {/* GST + receivables */}
              <div className="sb-grid sb-g2" style={{ marginTop: 14 }}>
                {this.renderGst(board.gst || {}, this.state.basis, kpi.billed || 0)}
                {this.renderReceivables(board.receivablesNow || [])}
              </div>

              {/* customers + minis */}
              <div className="sb-grid sb-g2" style={{ marginTop: 14 }}>
                {this.renderCustomers(board.customers || {}, board.customerMix || {})}
                {this.renderMinis(board)}
              </div>

              {this.renderDaily(board.daily || [])}
              {this.renderOrders()}
            </React.Fragment>
          ) : null}

          <p className="sb-foot2">
            Money spine = invoice-splits billed in the selected window. Tenders reflect how each payment was actually
            collected — payments booked as cash but carrying an online/UPI reference are reclassified so the split ties
            to collected. Status = live custody stage. Data cached up to 10 min.
          </p>
        </div>
      </div>
    );
  }

  renderStores(stores) {
    if (!stores.length || this.state.storeId !== 0) return null;
    stores = stores.slice().sort(byAmountDesc("billed"));
    const max = Math.max.apply(null, stores.map((s) => s.billed || 0).concat([1]));
    return (
      <div className="sb-card" style={{ marginTop: 14 }}>
        <div className="sb-sec-title">By store</div>
        <div className="sb-sec-note">Billed and collected per store · click a store to filter the whole board</div>
        <div className="sb-storelist">
          {stores.map((s) => (
            <div className="sb-storerow" key={s.id} onClick={() => this.setState({ storeId: s.id }, this.load)}>
              <div className="sb-storename">{s.name}<span className="sb-o"> · {s.orders} ord</span></div>
              <div className="sb-track"><div className="sb-fill" style={{ width: (s.billed / max) * 100 + "%" }} /></div>
              <div className="sb-storeamt">{INR(s.billed)}<span className="sb-owed2">{s.owed > 0 ? " · " + INR(s.owed) + " owed" : ""}</span></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderRevenue(rev) {
    if (!rev.length) return null;
    rev = rev.slice().sort(byAmountDesc("amount"));
    const max = Math.max.apply(null, rev.map((r) => r.amount || 0).concat([1]));
    return (
      <div className="sb-card" style={{ marginTop: 14 }}>
        <div className="sb-sec-title">Revenue by service</div>
        <div className="sb-sec-note">Share of the period's value by service (sums to Billed)</div>
        <div className="sb-svc">
          {rev.map((r, i) => (
            <div className="sb-svcrow" key={i}>
              <div className="sb-svctop">
                <div><div className="sb-nm">{r.service}</div><div className="sb-meta">{r.orders} ord · {r.pcs} pcs · {INR((r.amount || 0) / (r.pcs || 1))}/pc</div></div>
                <div className="sb-amt">{INR(r.amount)}</div>
              </div>
              <div className="sb-track"><div className="sb-fill" style={{ width: ((r.amount || 0) / max * 100).toFixed(1) + "%" }} /></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderTenders(tenders, collected) {
    if (!tenders.length) return null;
    tenders = tenders.slice().sort(byAmountDesc("amount"));
    const sum = tenders.reduce((s, t) => s + (t.amount || 0), 0) || 1;
    const C = 2 * Math.PI * 58;
    let off = 0;
    const arcs = tenders.map((t, i) => {
      const len = (t.amount || 0) / sum * C;
      const el = (
        <circle key={i} r="58" fill="none" stroke={TENDER_COLOR[t.mode] || "var(--slate)"} strokeWidth="20"
          strokeDasharray={len.toFixed(1) + " " + (C - len).toFixed(1)} strokeDashoffset={(-off).toFixed(1)} />
      );
      off += len;
      return el;
    });
    return (
      <div className="sb-card" style={{ marginTop: 14 }}>
        <div className="sb-sec-title">How the money came in</div>
        <div className="sb-sec-note">Collected tenders by mode (gateway-accurate)</div>
        <div className="sb-money">
          <svg width="170" height="170" viewBox="0 0 170 170" aria-label="tender donut">
            <g transform="translate(85,85) rotate(-90)">
              <circle r="58" fill="none" stroke="var(--track)" strokeWidth="20" />
              {arcs}
            </g>
            <text x="85" y="80" textAnchor="middle" style={{ fill: "var(--muted)", fontSize: 11 }}>Collected</text>
            <text x="85" y="99" textAnchor="middle" style={{ fill: "var(--ink)", fontSize: 16, fontWeight: 800 }}>{INRk(collected)}</text>
          </svg>
          <div className="sb-legend">
            {tenders.map((t, i) => (
              <div className="sb-li" key={i}>
                <span className="sb-dot" style={{ background: TENDER_COLOR[t.mode] || "var(--slate)" }} />
                <span className="sb-nm">{t.mode}</span>
                <span className="sb-val">{INR(t.amount)}</span>
                <span className="sb-pct">{Math.round((t.amount || 0) / sum * 100)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  renderPipeline(pipe) {
    if (!pipe.length) return null;
    pipe = pipe.slice().sort((a, b) => STAGE_ORDER.indexOf(a.stage) - STAGE_ORDER.indexOf(b.stage));
    const max = Math.max.apply(null, pipe.map((p) => (p.billed || 0)).concat([1]));
    return (
      <div className="sb-card" style={{ marginTop: 14 }}>
        <div className="sb-sec-title">Order pipeline (live)</div>
        <div className="sb-sec-note">Where billed orders sit now · <span style={{ color: "var(--green)", fontWeight: 700 }}>green = collected</span> / <span style={{ color: "var(--amber)", fontWeight: 700 }}>amber = still owed</span></div>
        <div className="sb-pl">
          {pipe.map((p, i) => {
            const val = p.billed || 0;
            const owed = Math.max(p.owed || 0, 0);
            const coll = Math.max(val - owed, 0);
            return (
              <div className="sb-plrow" key={i}>
                <div className="sb-pltop"><span className="sb-nm">{p.stage}</span><span className="sb-cnt">{p.count}</span></div>
                <div className="sb-bar" style={{ width: (val / max * 100).toFixed(1) + "%" }}>
                  <div className="sb-c" style={{ width: (val ? coll / val * 100 : 0) + "%" }} />
                  <div className="sb-o" style={{ width: (val ? owed / val * 100 : 0) + "%" }} />
                </div>
                <div className="sb-plfoot">
                  <span className="sb-plval">Value {INR(val)}</span>
                  <span><span className="sb-owed">Owed {INR(owed)}</span> · <span className="sb-coll">Collected {INR(coll)}</span></span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  renderGst(g, basis, headlineBilled) {
    const gstTotal = (g.cgst || 0) + (g.sgst || 0) + (g.igst || 0);
    const adj = g.adjustments || 0;
    // On the ORDER-date (intake) view, GST/build-up is a BILLING concept and reflects only the
    // invoiced portion of these orders — which is tiny for recently-placed orders and would
    // contradict the order-value headline. Show a clear note instead of a mismatched build-up.
    if (basis === "order") {
      const invoiced = g.grossBilled || 0;
      const pct = headlineBilled ? Math.round((invoiced / headlineBilled) * 100) : 0;
      return (
        <div className="sb-card">
          <div className="sb-sec-title">Billed build-up &amp; GST</div>
          <div className="sb-sec-note">GST is charged at invoicing, so it's a <b>billing-date</b> figure.</div>
          <div className="sb-gstnote">
            <p>You're viewing <b>Order date</b> (orders placed in the period). Of the {INR(headlineBilled)} placed, only <b>{INR(invoiced)}</b> ({pct}%) has been invoiced so far, on which <b>{INR(gstTotal)}</b> GST was collected — the rest bills as those orders complete.</p>
            <p>Switch to <b>Billing date</b> for the full component-by-component GST build-up.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="sb-card">
        <div className="sb-sec-title">Billed build-up &amp; GST</div>
        <div className="sb-sec-note">Every component that makes up Gross billed · CGST + SGST @ 9% each</div>
        <div style={{ marginTop: 14 }}>
          <div className="sb-lv"><span className="sb-k">Taxable value (services)</span><span className="sb-v">{INR(g.taxable)}</span></div>
          <div className="sb-lv sb-sub"><span className="sb-k">+ CGST</span><span className="sb-v">{INR(g.cgst)}</span></div>
          <div className="sb-lv sb-sub"><span className="sb-k">+ SGST</span><span className="sb-v">{INR(g.sgst)}</span></div>
          {g.igst > 0 ? <div className="sb-lv sb-sub"><span className="sb-k">+ IGST</span><span className="sb-v">{INR(g.igst)}</span></div> : null}
          <div className="sb-lv"><span className="sb-k">+ Packaging</span><span className="sb-v">{INR(g.packaging)}</span></div>
          <div className="sb-lv"><span className="sb-k">+ Delivery</span><span className="sb-v">{INR(g.delivery)}</span></div>
          <div className="sb-lv"><span className="sb-k">− Discount</span><span className="sb-v sb-neg">−{INR(g.discounts)}</span></div>
          {Math.round(adj) !== 0 ? (
            <div className="sb-lv"><span className="sb-k">{adj < 0 ? "−" : "+"} Adjustments <span className="sb-hint">(LK-blocked / rounding)</span></span><span className={"sb-v" + (adj < 0 ? " sb-neg" : "")}>{adj < 0 ? "−" : ""}{INR(Math.abs(adj))}</span></div>
          ) : null}
          <div className="sb-lv sb-total"><span className="sb-k">= Gross billed</span><span className="sb-v">{INR(g.grossBilled)}</span></div>
        </div>
        <div className="sb-sec-note" style={{ marginTop: 10 }}>GST collected this window: <b>{INR(gstTotal)}</b></div>
      </div>
    );
  }

  renderReceivables(age) {
    age = (age || []).slice().sort((a, b) => BUCKET_ORDER.indexOf(a.bucket) - BUCKET_ORDER.indexOf(b.bucket));
    const total = age.reduce((s, a) => s + (a.amount || 0), 0);
    const max = Math.max.apply(null, age.map((a) => a.amount || 0).concat([1]));
    return (
      <div className="sb-card">
        <div className="sb-sec-title">Open receivables (now)</div>
        <div className="sb-sec-note">{INR(total)} owed across the store, by age (last 120 days)</div>
        <div className="sb-age">
          {age.length ? age.map((a, i) => (
            <div className="sb-agerow" key={i}>
              <div className="sb-agelab"><span className="sb-k">{a.bucket}</span><span className="sb-o">{a.orders} ord</span></div>
              <div className="sb-track"><div className="sb-fill" style={{ width: ((a.amount || 0) / max * 100).toFixed(1) + "%", background: AGE_COLOR[a.bucket] || "var(--amber)" }} /></div>
              <div className="sb-ageamt">{INR(a.amount)}</div>
            </div>
          )) : <p className="sb-sec-note" style={{ marginTop: 14 }}>Nothing outstanding — all settled.</p>}
        </div>
      </div>
    );
  }

  renderCustomers(c, mix) {
    const top = (c.top || []).slice().sort(byAmountDesc("amount"));
    const nc = (mix && mix.newCust) || 0, rc = (mix && mix.repeatCust) || 0;
    const nv = (mix && mix.newValue) || 0, rv = (mix && mix.repeatValue) || 0;
    const ctot = nc + rc || 1, vtot = nv + rv || 1;
    return (
      <div className="sb-card sb-cust">
        <div className="sb-sec-title">Customers</div>
        <div style={{ marginTop: 12 }}><span className="sb-tot">{(c.total || 0).toLocaleString("en-IN")} <small>billed this window</small></span></div>
        {mix && (nc || rc) ? (
          <React.Fragment>
            <div className="sb-subhead">New vs repeat</div>
            <div className="sb-mixbar">
              <div className="sb-mixseg sb-mixnew" style={{ width: (nc / ctot * 100) + "%" }} />
              <div className="sb-mixseg sb-mixrep" style={{ width: (rc / ctot * 100) + "%" }} />
            </div>
            <div className="sb-mixrow"><span><span className="sb-mixdot sb-mixnew" /> New <b>{nc}</b> <span className="sb-o">({Math.round(nc / ctot * 100)}% · {INR(nv)}, {Math.round(nv / vtot * 100)}% of value)</span></span></div>
            <div className="sb-mixrow"><span><span className="sb-mixdot sb-mixrep" /> Repeat <b>{rc}</b> <span className="sb-o">({Math.round(rc / ctot * 100)}% · {INR(rv)}, {Math.round(rv / vtot * 100)}% of value)</span></span></div>
          </React.Fragment>
        ) : null}
        <div className="sb-subhead">Top customers</div>
        <div className="sb-list">
          {top.map((t, i) => (
            <div className="sb-ci" key={i}><span>{t.name || "—"} <span className="sb-o">· {t.orders} ord</span></span><span style={{ fontWeight: 700 }}>{INR(t.amount)}</span></div>
          ))}
        </div>
      </div>
    );
  }

  renderMinis(board) {
    const ch = board.channel || {};
    const coupons = board.coupons || 0;
    const delivered = (board.pipeline || []).find((p) => p.stage === "Delivered") || {};
    const chTot = (ch.pickup || 0) + (ch.walkin || 0) || 1;
    const pct = (n) => Math.round((n || 0) / chTot * 100);
    return (
      <div className="sb-grid" style={{ gridTemplateColumns: "1fr 1fr", alignContent: "start" }}>
        <div className="sb-card sb-mini"><div className="sb-lab">Walk-in vs pickup</div><div className="sb-rowline"><span>On-demand (pickup)</span><span>{ch.pickup || 0} · {pct(ch.pickup)}%</span></div><div className="sb-rowline"><span>Walk-in</span><span>{ch.walkin || 0} · {pct(ch.walkin)}%</span></div></div>
        <div className="sb-card sb-mini"><div className="sb-lab">Delivered orders</div><div className="sb-big">{(delivered.count || 0).toLocaleString("en-IN")}</div><div className="sb-sub">in this window</div></div>
        <div className="sb-card sb-mini"><div className="sb-lab">Coupons used</div><div className="sb-big">{coupons}</div><div className="sb-sub">orders with a promocode</div></div>
        <div className="sb-card sb-mini"><div className="sb-lab">Collection rate</div><div className="sb-big">{board.kpi && board.kpi.billed ? Math.round(board.kpi.collected / board.kpi.billed * 100) : 0}%</div><div className="sb-sub">of billed collected</div></div>
      </div>
    );
  }

  renderDaily(daily) {
    if (!daily.length) return null;
    daily = daily.slice().sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const W = 1080, H = 260, padL = 52, padR = 16, padT = 16, padB = 42;
    const iw = W - padL - padR, ih = H - padT - padB;
    const vals = daily.map((d) => d.amount || 0);
    const ymax = Math.max(Math.max.apply(null, vals.concat([1])) * 1.1, 1);
    const n = daily.length;
    const x = (i) => padL + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
    const y = (v) => padT + (1 - v / ymax) * ih;
    const pts = daily.map((d, i) => x(i).toFixed(1) + "," + y(d.amount || 0).toFixed(1)).join(" ");
    const gridlines = [];
    for (let g = 0; g <= 4; g++) {
      const gv = (ymax / 4) * g;
      const gy = y(gv);
      gridlines.push(<line key={"g" + g} x1={padL} y1={gy} x2={W - padR} y2={gy} />);
      gridlines.push(<text key={"gt" + g} x={padL - 8} y={gy + 4} textAnchor="end">{INRk(gv)}</text>);
    }
    const step = Math.max(1, Math.ceil(n / 10));
    // two-line label: weekday on top, date below
    const xlabels = daily.map((d, i) => {
      if (!(i % step === 0 || i === n - 1)) return null;
      const dt = new Date(d.date + "T12:00:00");
      const wd = isNaN(dt) ? "" : WD[dt.getDay()];
      const dm = isNaN(dt) ? d.date : dt.getDate() + " " + dt.toLocaleDateString("en-IN", { month: "short" });
      return (
        <g key={"x" + i}>
          <text x={x(i)} y={H - 20} textAnchor="middle" style={{ fontWeight: 700 }}>{wd}</text>
          <text x={x(i)} y={H - 7} textAnchor="middle">{dm}</text>
        </g>
      );
    });
    return (
      <div className="sb-card" style={{ marginTop: 14 }}>
        <div className="sb-sec-title">Daily billing</div>
        <div className="sb-chart">
          <svg width="100%" viewBox={"0 0 " + W + " " + H} preserveAspectRatio="none" aria-label="daily billing">
            <defs>
              <linearGradient id="sbfill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="var(--accent)" stopOpacity="0.28" />
                <stop offset="1" stopColor="var(--accent)" stopOpacity="0.02" />
              </linearGradient>
            </defs>
            <g className="sb-gridlines">{gridlines}</g>
            <polygon points={padL + "," + y(0) + " " + pts + " " + (W - padR) + "," + y(0)} fill="url(#sbfill)" />
            <polyline points={pts} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" />
            {daily.map((d, i) => <circle key={"c" + i} cx={x(i)} cy={y(d.amount || 0)} r={i === n - 1 ? 4 : 0} fill="var(--accent)" />)}
            {xlabels}
          </svg>
        </div>
      </div>
    );
  }

  renderOrders() {
    const b = this.state.board;
    const all = (b && b.orders) || [];
    const rows = this.tableRows();
    const storeNames = Array.from(new Set(all.map((o) => o.store).filter(Boolean))).sort();
    const arrow = (k) => (this.state.sort === k ? (this.state.dir === 1 ? " ▲" : " ▼") : "");
    return (
      <div className="sb-card" style={{ marginTop: 14 }}>
        <div className="sb-sec-title">Order lines — live status</div>
        <div className="sb-sec-note">One row per service line (a split) · each has its own stage &amp; amount · overdue flagged in red</div>
        <div className="sb-filters">
          <input placeholder="Search order #, customer, mobile, store, service…" value={this.state.q} onChange={(e) => this.setState({ q: e.target.value })} />
          <select value={this.state.tableStore} onChange={(e) => this.setState({ tableStore: e.target.value })}>
            <option value="">All stores</option>
            {storeNames.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={this.state.tablePay} onChange={(e) => this.setState({ tablePay: e.target.value })}>
            <option value="">Any payment</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </select>
          <select value={this.state.tableStatus} onChange={(e) => this.setState({ tableStatus: e.target.value })}>
            <option value="">Any status</option>
            {STAGE_ORDER.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="sb-tg" aria-pressed={this.state.odOnly} onClick={() => this.setState((s) => ({ odOnly: !s.odOnly }))}>⚠ Overdue only</button>
        </div>
        <div className="sb-tablewrap">
          <table>
            <thead>
              <tr>
                <th onClick={() => this.onSort("id")}>Order{arrow("id")}</th>
                <th onClick={() => this.onSort("store")}>Store{arrow("store")}</th>
                <th onClick={() => this.onSort("customer")}>Customer{arrow("customer")}</th>
                <th onClick={() => this.onSort("service")}>Service{arrow("service")}</th>
                <th onClick={() => this.onSort("billed")}>Billed{arrow("billed")}</th>
                <th className="sb-r" onClick={() => this.onSort("amount")}>Amount ₹{arrow("amount")}</th>
                <th onClick={() => this.onSort("paid")}>Payment{arrow("paid")}</th>
                <th title="Physical stock status — shown for unpaid lines only">Stock</th>
                <th onClick={() => this.onSort("stage")}>Status{arrow("stage")}</th>
                <th onClick={() => this.onSort("delivery")}>Delivery{arrow("delivery")}</th>
                <th className="sb-r" onClick={() => this.onSort("age")}>Age{arrow("age")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((o, i) => (
                <tr key={i} className={o.overdue ? "sb-od" : ""}>
                  <td className="sb-oid">#{o.id}<span className="sb-s">{o.garments} pcs</span></td>
                  <td>{o.store}</td>
                  <td>{o.customer}<span className="sb-mob">{o.mobile}</span></td>
                  <td>{o.service}</td>
                  <td>{ddow(o.billed)}</td>
                  <td className="sb-r">{(o.amount || 0).toLocaleString("en-IN")}</td>
                  <td><span className={"sb-pill " + (o.paid ? "sb-paid" : "sb-unpaid")}>{o.paid ? "Paid" : "Unpaid"}</span></td>
                  <td>{o.inStock === 1 ? <span className="sb-pill sb-in">In stock</span>
                      : o.inStock === 0 ? <span className="sb-pill sb-out">Not in stock</span>
                      : o.inStock === -1 ? <span className="sb-nmk">Not marked</span>
                      : <span className="sb-nmk">—</span>}</td>
                  <td><span className="sb-badge" style={{ "--sc": "var(" + (STAGE_COLOR[o.stage] || "--slate") + ")" }}><span className="sb-bdot" />{o.stage}</span></td>
                  <td>{o.stage === "Delivered" ? <span className="sb-done">Delivered {ddow(o.delivery)}</span> : (o.overdue ? <span className="sb-odt">{ddow(o.delivery)} · overdue</span> : ddow(o.delivery))}</td>
                  <td className="sb-r">{o.age}d</td>
                </tr>
              )) : (
                <tr><td colSpan="11" style={{ textAlign: "center", color: "var(--muted)", padding: 36 }}>No order lines match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="sb-foot">
          Showing <b>{rows.length}</b> of {all.length} order lines{all.length >= 2000 ? " (capped at 2000 — narrow the range)" : ""} ·
          each row = one service line with its own custody stage (Picked up → At store → Sent to factory → At factory → Returning → Ready → Payment pending → Delivered) · overdue = promised date passed, not delivered · "pcs" = garments in that line.
        </div>
      </div>
    );
  }
}

const CSS = `
.sb-root{--bg:#f8f4f0;--card:#ffffff;--ink:#2a1230;--muted:#8b7d92;--line:#ece4ec;--soft:#f7f0f4;
  --accent:#fe1d7f;--accent2:#6c2b8f;--accent-weak:#ffe4f0;
  --green:#128c4b;--amber:#c99a3c;--gold:#c99a3c;--red:#d92d20;
  --slate:#64748b;--violet:#7c3aed;--blue:#2563eb;--track:#efe6ec;
  --shadow:0 1px 2px rgba(42,18,48,.05),0 6px 20px rgba(42,18,48,.06);
  font-family:"Plus Jakarta Sans","Segoe UI",system-ui,sans-serif;font-size:14px;line-height:1.45;
  color:var(--ink);font-variant-numeric:tabular-nums;background:var(--bg)}
.sb-wrap{max-width:1180px;margin:0 auto;padding:12px 8px 70px}
.sb-eyebrow{color:var(--accent);font-weight:700;font-size:12px;letter-spacing:.08em;text-transform:uppercase;margin:6px 0 6px}
.sb-h2{font-size:22px;font-weight:800;letter-spacing:-.01em;margin:0}.sb-dim{color:var(--muted);font-weight:600}
.sb-lede{color:var(--muted);margin:6px 0 0;max-width:820px}
.sb-basis{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin:16px 0 2px}
.sb-basis-lab{font-size:11px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);font-weight:700}
.sb-seg{display:inline-flex;border:1px solid var(--line);border-radius:999px;background:var(--soft);padding:3px}
.sb-segbtn{border:0;background:transparent;color:var(--muted);font:inherit;font-weight:700;padding:6px 16px;border-radius:999px;cursor:pointer}
.sb-segbtn[aria-pressed="true"]{background:var(--accent);color:#fff}
.sb-basis-note{color:var(--muted);font-size:12px}
.sb-basis-chip{display:inline-block;margin-left:8px;background:var(--accent-weak);color:var(--accent);font-weight:700;font-size:11px;letter-spacing:.03em;text-transform:uppercase;padding:2px 9px;border-radius:999px;vertical-align:middle}
.sb-controls{display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin:18px 0 6px}
.sb-rchip{border:1px solid var(--line);background:var(--card);border-radius:999px;padding:8px 16px;font:inherit;font-weight:600;color:var(--muted);cursor:pointer}
.sb-rchip[aria-pressed="true"]{background:var(--accent);border-color:var(--accent);color:#fff}
.sb-crow{display:flex;flex-wrap:wrap;gap:10px;align-items:flex-end;margin:12px 0 2px}
.sb-dash{align-self:center;color:var(--muted);padding-bottom:9px}
.sb-field label{display:block;font-size:11px;letter-spacing:.05em;text-transform:uppercase;color:var(--muted);margin-bottom:5px;font-weight:600}
.sb-field input,.sb-field select{border:1px solid var(--line);background:var(--card);color:var(--ink);border-radius:10px;padding:9px 12px;font:inherit}
.sb-apply{background:var(--accent);color:#fff;border:0;border-radius:999px;padding:11px 26px;font:inherit;font-weight:700;cursor:pointer}
.sb-apply:disabled{opacity:.6;cursor:default}
.sb-showing{color:var(--muted);font-size:13px;margin:10px 0 0}
.sb-err{background:#fff0ee;border:1px solid #f5c6c0;color:var(--red);border-radius:12px;padding:12px 16px;margin-top:14px;font-weight:600}
.sb-loading{color:var(--muted);padding:40px 0;text-align:center}
.sb-grid{display:grid;gap:14px}
.sb-g3{grid-template-columns:repeat(3,1fr)}.sb-g2{grid-template-columns:repeat(2,1fr)}
@media(max-width:860px){.sb-g3,.sb-g2{grid-template-columns:1fr}}
.sb-card{background:var(--card);border:1px solid var(--line);border-radius:16px;box-shadow:var(--shadow);padding:20px 22px}
.sb-kpi .sb-lab{color:var(--muted);font-size:12px;letter-spacing:.06em;text-transform:uppercase;font-weight:700}
.sb-kpi .sb-big{font-size:32px;font-weight:800;letter-spacing:-.02em;margin-top:10px;line-height:1}
.sb-kpi .sb-sub{color:var(--muted);font-size:13px;margin-top:8px}
.sb-sec-title{color:var(--muted);font-size:12px;letter-spacing:.07em;text-transform:uppercase;font-weight:700}
.sb-sec-note{color:var(--muted);font-size:12.5px;margin-top:3px}
.sb-svc{margin-top:8px}.sb-svcrow{margin-top:16px}
.sb-svctop{display:flex;justify-content:space-between;align-items:baseline;gap:12px}
.sb-nm{font-weight:700}.sb-meta{color:var(--muted);font-size:12px;margin-top:1px}
.sb-amt{font-weight:800;font-size:15px}
.sb-track{height:12px;border-radius:8px;background:var(--track);margin-top:8px;overflow:hidden}
.sb-fill{height:100%;border-radius:8px;background:var(--accent)}
.sb-storelist{margin-top:8px}
.sb-storerow{display:grid;grid-template-columns:1fr 2fr auto;gap:14px;align-items:center;padding:9px 0;border-bottom:1px solid var(--line);cursor:pointer}
.sb-storerow:last-child{border-bottom:0}
.sb-storerow:hover{background:var(--soft)}
.sb-storename{font-weight:700}.sb-storename .sb-o{color:var(--muted);font-weight:500;font-size:12px}
.sb-storeamt{font-weight:800;text-align:right;white-space:nowrap}
.sb-owed2{color:var(--amber);font-weight:700;font-size:12px}
.sb-money{display:flex;gap:28px;align-items:center;flex-wrap:wrap;margin-top:14px}
.sb-legend{flex:1;min-width:240px}
.sb-li{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--line)}
.sb-li:last-child{border-bottom:0}
.sb-dot{width:11px;height:11px;border-radius:50%}
.sb-legend .sb-nm{font-weight:600}.sb-val{margin-left:auto;font-weight:800}
.sb-pct{color:var(--muted);width:44px;text-align:right}
.sb-pl .sb-plrow{margin-top:18px}
.sb-pltop{display:flex;align-items:center;gap:9px}
.sb-cnt{background:var(--accent-weak);color:var(--accent);font-weight:700;font-size:12px;border-radius:999px;padding:2px 10px}
.sb-bar{height:14px;border-radius:8px;background:var(--track);margin-top:9px;display:flex;overflow:hidden;min-width:4px}
.sb-bar .sb-c{background:var(--green)}.sb-bar .sb-o{background:var(--amber)}
.sb-plfoot{display:flex;justify-content:space-between;margin-top:7px;font-size:12.5px}
.sb-plval{color:var(--muted)}.sb-owed{color:var(--amber);font-weight:700}.sb-coll{color:var(--green);font-weight:700}
.sb-lv{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px dashed var(--line)}
.sb-lv:last-child{border-bottom:0}.sb-lv .sb-k{color:var(--muted)}.sb-lv .sb-v{font-weight:700}
.sb-lv.sb-total{border-top:2px solid var(--line);border-bottom:0;margin-top:4px;padding-top:12px}
.sb-lv.sb-total .sb-k,.sb-lv.sb-total .sb-v{font-weight:800;color:var(--ink)}
.sb-lv.sb-sub{padding:4px 0 4px 14px;border-bottom:0}
.sb-lv.sb-sub .sb-k,.sb-lv.sb-sub .sb-v{color:var(--muted);font-weight:600;font-size:13px}
.sb-hint{color:var(--muted);font-weight:500;font-size:11px}
.sb-kpi-note{color:var(--muted);font-size:12.5px;margin:12px 2px 0}
.sb-gstnote{margin-top:16px;padding:14px 16px;background:var(--soft);border:1px solid var(--line);border-radius:12px}
.sb-gstnote p{margin:0 0 10px;color:var(--ink);font-size:13px;line-height:1.5}
.sb-gstnote p:last-child{margin-bottom:0;color:var(--muted)}
.sb-neg{color:var(--red)}
.sb-subhead{color:var(--muted);font-size:11px;letter-spacing:.06em;text-transform:uppercase;font-weight:700;margin:16px 0 4px}
.sb-age{margin-top:8px}.sb-agerow{margin-top:14px}
.sb-agelab{display:flex;justify-content:space-between}.sb-agelab .sb-k{font-weight:600}.sb-agelab .sb-o{color:var(--muted);font-size:12px}
.sb-ageamt{font-weight:800;margin-top:6px;text-align:right}
.sb-cust .sb-tot{font-size:30px;font-weight:800}.sb-cust .sb-tot small{font-size:14px;color:var(--muted);font-weight:600}
.sb-list{margin-top:8px}
.sb-ci{display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid var(--line)}
.sb-ci:last-child{border-bottom:0}.sb-ci .sb-o{color:var(--muted);font-weight:500;font-size:12px}
.sb-mixbar{display:flex;height:12px;border-radius:8px;overflow:hidden;background:var(--track);margin:8px 0 10px}
.sb-mixseg{height:100%}
.sb-mixseg.sb-mixnew{background:var(--green)}.sb-mixseg.sb-mixrep{background:var(--accent)}
.sb-mixrow{display:flex;justify-content:space-between;padding:5px 0;font-weight:600}
.sb-mixrow .sb-o{color:var(--muted);font-weight:500;font-size:12px}
.sb-mixdot{display:inline-block;width:9px;height:9px;border-radius:50%;margin-right:7px}
.sb-mixdot.sb-mixnew{background:var(--green)}.sb-mixdot.sb-mixrep{background:var(--accent)}
.sb-mini .sb-lab{color:var(--muted);font-size:11px;letter-spacing:.06em;text-transform:uppercase;font-weight:700}
.sb-mini .sb-big{font-size:26px;font-weight:800;margin-top:8px}.sb-mini .sb-sub{color:var(--muted);font-size:12.5px;margin-top:6px}
.sb-rowline{display:flex;justify-content:space-between;margin-top:8px;font-weight:700}
.sb-chart{margin-top:10px}
.sb-gridlines line{stroke:var(--line)}
.sb-chart svg text{fill:var(--muted);font-size:11px}
.sb-filters{display:flex;gap:9px;flex-wrap:wrap;align-items:center;margin:14px 0 14px}
.sb-filters input{flex:1;min-width:200px;border:1px solid var(--line);background:var(--card);color:var(--ink);border-radius:10px;padding:9px 12px;font:inherit}
.sb-filters select,.sb-filters .sb-tg{border:1px solid var(--line);background:var(--card);color:var(--ink);border-radius:10px;padding:9px 12px;font:inherit;cursor:pointer}
.sb-filters .sb-tg[aria-pressed="true"]{background:rgba(217,45,32,.12);color:var(--red);border-color:var(--red)}
.sb-tablewrap{overflow-x:auto;border:1px solid var(--line);border-radius:14px}
.sb-root table{width:100%;border-collapse:collapse;min-width:920px}
.sb-root thead th{position:sticky;top:0;background:var(--card);text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.04em;color:var(--muted);font-weight:700;padding:11px 14px;border-bottom:1.5px solid var(--line);cursor:pointer;white-space:nowrap}
.sb-root th.sb-r,.sb-root td.sb-r{text-align:right}
.sb-root tbody td{padding:11px 14px;border-bottom:1px solid var(--line);vertical-align:top}
.sb-root tbody tr:last-child td{border-bottom:0}
.sb-root tbody tr.sb-od{background:linear-gradient(90deg,rgba(217,45,32,.08),transparent 55%)}
.sb-root tbody tr.sb-od td:first-child{box-shadow:inset 3px 0 0 var(--red)}
.sb-oid{font-weight:700}.sb-oid .sb-s{display:block;color:var(--muted);font-size:12px;font-weight:500}
.sb-mob{display:block;color:var(--muted);font-size:12px}
.sb-badge{display:inline-flex;align-items:center;gap:6px;padding:3px 9px;border-radius:999px;font-size:12px;font-weight:700;white-space:nowrap;color:var(--sc);background:rgba(100,116,139,.14)}
.sb-badge .sb-bdot{width:7px;height:7px;border-radius:50%;background:currentColor}
.sb-pill{display:inline-block;padding:2px 8px;border-radius:6px;font-size:11.5px;font-weight:700}
.sb-pill.sb-paid{color:var(--green);background:rgba(18,140,75,.14)}
.sb-pill.sb-unpaid{color:var(--red);background:rgba(217,45,32,.12)}
.sb-pill.sb-in{color:var(--green);background:rgba(18,140,75,.14)}
.sb-pill.sb-out{color:var(--red);background:rgba(217,45,32,.12)}
.sb-nmk{color:var(--muted);font-size:11.5px}
.sb-odt{color:var(--red);font-weight:700}.sb-done{color:var(--muted)}
.sb-foot{color:var(--muted);font-size:12px;margin-top:14px;line-height:1.6}
.sb-foot2{color:var(--muted);font-size:12px;margin-top:24px;line-height:1.6;max-width:900px}
`;
